/**
 * @author Radek Liška <ing.radek.liska@gmail.com>
 * @author Vladislav Urbánek <urbanek.vlada@gmail.com>
 * @copyright Ki-Wi Digital s.r.o.
 * @license
 * All Rights Reserved.
 * Unauthorized copying of this file is strictly prohibited.
 * Proprietary and confidential.
 */

// @ts-check

import EventEmitter from 'eventemitter3';
import isNil from 'lodash/isNil';

import PdfParent from './pdf-parent';

/**
 * @import { IElement } from '../iframe'
 * @import { EventTypes, ViewerController } from '../viewer/controller'
 * @import ScheduleInfo from '../schedule/schedule-info'
 * @import ViewerLifeCycleFactory from '../viewer/life-cycle/viewer-life-cycle-factory'
 */

// TODO #295 Nejasné rozdělení odpovědnosti mezi ovladačem a PdfParent:
// - Oba přistupují k elementů. Kdo má co dělat?
// - Ovladač zatím vždy řešil pozicování, tak by k němu měl mít přístup.
// - PdfParent potřebuje iframe pro komunikaci, takže zde je také přístup požadovaný.
/**
 * Ovladač pro zobrazování PDF souborů.
 * @implements {ViewerController}
 */
export default class PdfViewerController {
  /**
   * Vytvoří instanci.
   * @param {IElement} el
   * @param {string} origin Origin, který budou mít zprávy od potomka.
   * @param {number} timeout Maximální doba čekání na navázání komunikace. [ms]
   * @param {ViewerLifeCycleFactory} viewerLifeCycleFactory
   */
  constructor(el, origin, timeout, viewerLifeCycleFactory) {
    this.el = el;
    this.origin = origin;
    this.timeout = timeout;
    /**
     * Správce událostí
     * @private
     * @type {EventEmitter<EventTypes>}
     */
    this.emitter = new EventEmitter();
    this.onDurationchange = this.onDurationchange.bind(this);
    this.onElementError = this.onElementError.bind(this);
    this.onEnded = this.onEnded.bind(this);
    this.onParentError = this.onParentError.bind(this);
    this.parent = null;
    /** @type {ScheduleInfo | null} */
    this.scheduleInfo = null;
    this.viewerLifeCycle = viewerLifeCycleFactory.create();
  }

  /**
   * Naváže naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   */
  bindElement(el) {
    el.addEventListener('error', this.onElementError);
  }

  /**
   * Naváže naslouchání událostem z PDF přehrávače.
   * @param {PdfParent} parent
   * @private
   */
  bindParent(parent) {
    parent.on('durationchange', this.onDurationchange);
    parent.on('ended', this.onEnded);
    parent.on('error', this.onParentError);
  }

  /** @type {ViewerController['destroy']} */
  destroy() {
    this.scheduleInfo?.releaseMaybeBlobUrl();
    const { parent } = this;
    if (!isNil(parent)) {
      this.unbindParent(parent);
    }
    this.unbindElement(this.el);
  }

  /** @type {ViewerController['enter']} */
  enter() {
    return this.viewerLifeCycle.enter(async () => {
      // Spuštění přehrávání.
      await this.parent?.play().then(() => this.emitter.emit('playing'));
    });
  }

  /** @type {ViewerController['leave']} */
  leave() {
    return this.viewerLifeCycle.leave(async () => {
      // Zastavení přehrávání.
      await this.parent?.pause();
    });
  }

  /** @type {ViewerController['off']} */
  off(...args) {
    this.emitter.off(...args);
  }

  /** @type {ViewerController['off']} */
  on(...args) {
    this.emitter.on(...args);
  }

  /**
   * Reakce na změnu doby přehrávání.
   * @param {number} duration Délka přehrávání. [s]
   * @private
   */
  onDurationchange(duration) {
    const requestedDuration = this.scheduleInfo?.getPlayDuration();
    if (!requestedDuration) {
      // Pokud je požadovaná doba přehrávání nulová, použije se výchozí doba přehrávání.
      return;
    }
    // Dle skutečné a požadované doby přehrávání se určí rychlost.
    const playbackRate = (duration * 1000) / requestedDuration;
    return this.parent?.setPlaybackRate(playbackRate);
  }

  /**
   * Reakce na chybu v elementu.
   * @private
   */
  onElementError() {
    const error = new Error('Unknown HTMLIframeElement error.');
    this.emitter.emit('error', error);
  }

  /**
   * Dosažení konce přehrávání.
   * @private
   */
  onEnded() {
    this.emitter.emit('played');
  }

  /**
   * Reakce na chybu z PDF přehrávače.
   * @param {Error} error
   * @private
   */
  onParentError(error) {
    this.emitter.emit('error', error);
  }

  /** @type {ViewerController['release']} */
  release() {
    return this.viewerLifeCycle.release(() => {
      this.parent?.destroy();
      this.scheduleInfo?.releaseMaybeBlobUrl();
      this.unbindElement(this.el);
      // Zrušení hodnot v instanci.
      this.scheduleInfo = null;
    });
  }

  /** @type {ViewerController['seize']} */
  seize(scheduleInfo) {
    return this.viewerLifeCycle.seize(() => {
      // Nastavení hodnot v instanci.
      this.scheduleInfo = scheduleInfo;
      // Určení všech potřebných hodnot.
      return scheduleInfo.getUrl().then((url) => {
        const { el } = this;
        // Příprava zdrojového elementu.
        this.bindElement(el);
        el.classList.add('pdf-viewer-controller');
        el.sandbox = 'allow-forms allow-same-origin allow-scripts';
        // Deaktivace scrollování v iframe.
        // Označeno jako deprecated, ale některé prohlížeče ho vyžadují.
        el.scrolling = 'no';
        const parent = new PdfParent(el, this.origin, this.timeout);
        this.parent = parent;
        this.bindParent(parent);
        return parent.setSrc(url);
      });
    });
  }

  /**
   * Uvolní naslouchání událostem z elementu.
   * @param {HTMLIFrameElement} el Element, kterému se naslouchá.
   * @private
   */
  unbindElement(el) {
    el.removeEventListener('error', this.onElementError);
  }

  /**
   * Uvolní naslouchání událostem z PDF serveru.
   * @param {PdfParent} parent
   * @private
   */
  unbindParent(parent) {
    parent.off('durationchange', this.onDurationchange);
    parent.off('ended', this.onEnded);
    parent.off('error', this.onParentError);
  }
}
